import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { useSettings } from '../hooks/useSettings';
import { useRuntimeCapabilities } from '../hooks/useRuntimeCapabilities';
import { useTenant } from '../contexts/TenantContext';
import { buildApiUrl } from '../services/api';

interface WhatsAppSession {
  name: string;
  displayName?: string;
  status: 'WORKING' | 'SCAN_QR_CODE' | 'STOPPED' | 'FAILED';
  provider: 'WAHA' | 'EVOLUTION';
  qr?: string;
  qrExpiresAt?: Date;
  me?: {
    id: string;
    pushName: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

function QRCountdown({ expiresAt }: { expiresAt: Date }) {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = expiresAt.getTime();
      const difference = expiry - now;
      return Math.max(0, Math.ceil(difference / 1000));
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  if (timeLeft <= 0) {
    return <span className="text-sm text-red-600">QR Code expirado</span>;
  }

  return <span className="text-sm text-orange-600">Expira em {timeLeft}s</span>;
}

const formatWhatsAppNumber = (value?: string) => {
  if (!value) return 'Não conectado';
  const digits = value.replace(/\D/g, '');
  if (digits.length < 12) return value;

  const country = digits.slice(0, 2);
  const area = digits.slice(2, 4);
  const first = digits.length === 13 ? digits.slice(4, 9) : digits.slice(4, 8);
  const last = digits.length === 13 ? digits.slice(9, 13) : digits.slice(8, 12);

  return `+${country} ${area} ${first}-${last}`;
};

const formatOnlineTime = (updatedAt?: string, status?: string) => {
  if (!updatedAt || status !== 'WORKING') {
    return 'Aguardando conexão';
  }

  const started = new Date(updatedAt).getTime();
  const now = Date.now();
  const diffMinutes = Math.max(0, Math.floor((now - started) / 60000));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours <= 0) {
    return `${minutes}min`;
  }

  return `${hours}h ${minutes}min`;
};

const getStatusBadge = (status: WhatsAppSession['status']) => {
  switch (status) {
    case 'WORKING':
      return { label: 'Conectado', className: 'bg-emerald-600 text-white' };
    case 'SCAN_QR_CODE':
      return { label: 'Aguardando QR', className: 'bg-amber-500 text-white' };
    case 'FAILED':
      return { label: 'Erro', className: 'bg-red-600 text-white' };
    default:
      return { label: 'Desconectado', className: 'bg-slate-400 text-white' };
  }
};

export function WhatsAppConnectionsPage() {
  const { settings } = useSettings();
  const { selectedTenantId, loading: tenantLoading } = useTenant();
  const { capabilities } = useRuntimeCapabilities(selectedTenantId);
  const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionProvider, setNewSessionProvider] = useState<'WAHA' | 'EVOLUTION'>('EVOLUTION');
  const [isCreating, setIsCreating] = useState(false);
  const [createSessionModalOpen, setCreateSessionModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [currentQRSession, setCurrentQRSession] = useState<WhatsAppSession | null>(null);
  const [loadingBySession, setLoadingBySession] = useState<Record<string, string>>({});
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
  const [confirmDeleteSession, setConfirmDeleteSession] = useState<WhatsAppSession | null>(null);
  const inboxPollInterval = capabilities.recommendations.inboxPollingIntervalMs;

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(buildApiUrl(url), {
      ...options,
      headers,
    });
  };

  const sortedSessions = useMemo(
    () =>
      [...sessions].sort((a, b) => {
        const aPriority = a.status === 'WORKING' ? 0 : a.status === 'SCAN_QR_CODE' ? 1 : 2;
        const bPriority = b.status === 'WORKING' ? 0 : b.status === 'SCAN_QR_CODE' ? 1 : 2;
        if (aPriority !== bPriority) return aPriority - bPriority;
        return (b.updatedAt ? new Date(b.updatedAt).getTime() : 0) - (a.updatedAt ? new Date(a.updatedAt).getTime() : 0);
      }),
    [sessions]
  );

  useEffect(() => {
    if (tenantLoading || !selectedTenantId) {
      return;
    }

    loadSessions();

    const interval = setInterval(() => {
      loadSessions(false);
    }, inboxPollInterval);

    return () => clearInterval(interval);
  }, [selectedTenantId, tenantLoading, inboxPollInterval]);

  useEffect(() => {
    if (!qrModalOpen || !currentQRSession) {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const response = await authenticatedFetch('/waha/sessions');
        if (!response.ok) return;

        const data = await response.json();
        const updated = data.find((item: WhatsAppSession) => item.name === currentQRSession.name);

        if (updated?.status === 'WORKING') {
          setQrModalOpen(false);
          setCurrentQRSession(null);
          await loadSessions(false);
          toast.success(`Conexão confirmada para ${updated.me?.pushName || updated.displayName || updated.name}`);
        }
      } catch (error) {
        console.error('Erro ao verificar status da conexão:', error);
      }
    }, inboxPollInterval);

    return () => clearInterval(interval);
  }, [qrModalOpen, currentQRSession, inboxPollInterval]);

  useEffect(() => {
    const closeMenu = () => setOpenMenuFor(null);
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, []);

  const setSessionLoading = (sessionName: string, state: string | null) => {
    setLoadingBySession((current) => {
      const next = { ...current };
      if (state) {
        next[sessionName] = state;
      } else {
        delete next[sessionName];
      }
      return next;
    });
  };

  const loadSessions = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }

      const response = await authenticatedFetch('/waha/sessions');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setSessions(
        data.map((session: any) => ({
          name: session.name,
          displayName: session.displayName,
          status: session.status || 'STOPPED',
          provider: session.provider || 'EVOLUTION',
          me: session.me || undefined,
          qr: session.qr || undefined,
          qrExpiresAt: session.qrExpiresAt ? new Date(session.qrExpiresAt) : undefined,
          createdAt: session.createdAt,
          updatedAt: session.updatedAt,
        }))
      );
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
      if (showLoading) {
        toast.error('Erro ao carregar conexões WhatsApp');
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const createSession = async () => {
    if (!newSessionName.trim()) {
      toast.error('Digite um nome para a sessão');
      return false;
    }

    if (newSessionProvider === 'WAHA' && (!settings?.wahaHost || !settings?.wahaApiKey)) {
      toast.error('Configure as credenciais WAHA nas configurações do sistema');
      return false;
    }

    if (newSessionProvider === 'EVOLUTION' && (!settings?.evolutionHost || !settings?.evolutionApiKey)) {
      toast.error('Configure as credenciais Evolution nas configurações do sistema');
      return false;
    }

    setIsCreating(true);
    try {
      const response = await authenticatedFetch('/waha/sessions', {
        method: 'POST',
        body: JSON.stringify({
          name: newSessionName.trim(),
          provider: newSessionProvider,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      toast.success('Sessão criada com sucesso');
      setNewSessionName('');
      setNewSessionProvider('EVOLUTION');
      setCreateSessionModalOpen(false);
      await loadSessions();
      return true;
    } catch (error) {
      console.error('Erro ao criar sessão:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao criar sessão');
      return false;
    } finally {
      setIsCreating(false);
    }
  };

  const openQRModal = async (sessionName: string) => {
    try {
      const currentSession = sessions.find((session) => session.name === sessionName);
      if (!currentSession) return;

      if (currentSession.qr && currentSession.qrExpiresAt && currentSession.qrExpiresAt > new Date()) {
        setCurrentQRSession(currentSession);
        setQrModalOpen(true);
        return;
      }

      const qrResponse = await authenticatedFetch(`/waha/sessions/${sessionName}/auth/qr`);
      if (!qrResponse.ok) {
        const errorData = await qrResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao buscar QR Code');
      }

      const qrData = await qrResponse.json();
      setCurrentQRSession({
        ...currentSession,
        qr: qrData.qr,
        qrExpiresAt: qrData.expiresAt ? new Date(qrData.expiresAt) : new Date(Date.now() + 60000),
      });
      setQrModalOpen(true);
    } catch (error) {
      console.error('Erro ao abrir QR Code:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao abrir QR Code');
    }
  };

  const reconnectSession = async (session: WhatsAppSession) => {
    setSessionLoading(session.name, 'reconnect');
    try {
      const response = await authenticatedFetch(`/waha/sessions/${session.name}/start`, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      if (session.status === 'STOPPED' || session.status === 'FAILED' || session.status === 'SCAN_QR_CODE') {
        await openQRModal(session.name);
      }

      toast.success(`Reconexão iniciada para ${session.me?.pushName || session.displayName || session.name}`);
      await loadSessions(false);
    } catch (error) {
      console.error('Erro ao reconectar sessão:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao reconectar sessão');
    } finally {
      setSessionLoading(session.name, null);
    }
  };

  const refreshSession = async (sessionName: string) => {
    setSessionLoading(sessionName, 'refresh');
    try {
      await loadSessions(false);
      toast.success('Sessão atualizada');
    } finally {
      setSessionLoading(sessionName, null);
    }
  };

  const restartSession = async (sessionName: string) => {
    setOpenMenuFor(null);
    setSessionLoading(sessionName, 'restart');
    try {
      const response = await authenticatedFetch(`/waha/sessions/${sessionName}/restart`, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Sessão reiniciada');
      await loadSessions(false);
    } catch (error) {
      console.error('Erro ao reiniciar sessão:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao reiniciar sessão');
    } finally {
      setSessionLoading(sessionName, null);
    }
  };

  const disconnectSession = async (sessionName: string) => {
    setOpenMenuFor(null);
    setSessionLoading(sessionName, 'disconnect');
    try {
      const response = await authenticatedFetch(`/waha/sessions/${sessionName}/stop`, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Sessão desconectada');
      await loadSessions(false);
    } catch (error) {
      console.error('Erro ao desconectar sessão:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao desconectar sessão');
    } finally {
      setSessionLoading(sessionName, null);
    }
  };

  const deleteSession = async () => {
    if (!confirmDeleteSession) return;

    const sessionName = confirmDeleteSession.name;
    setSessionLoading(sessionName, 'delete');
    try {
      const response = await authenticatedFetch(`/waha/sessions/${sessionName}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Sessão excluída com sucesso');
      setConfirmDeleteSession(null);
      await loadSessions(false);
    } catch (error) {
      console.error('Erro ao excluir sessão:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao excluir sessão');
    } finally {
      setSessionLoading(sessionName, null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-emerald-600" />
        <span className="ml-2 text-gray-600">Carregando conexões...</span>
      </div>
    );
  }

  return (
    <>
      <Header
        title="Conexões WhatsApp"
        subtitle={`${sessions.length} ${sessions.length === 1 ? 'sessão ativa' : 'sessões ativas'}`}
        actions={
          <button
            onClick={() => setCreateSessionModalOpen(true)}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            + Nova sessão
          </button>
        }
      />

      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 pb-8 pt-6">
        {sortedSessions.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center text-slate-500 shadow-sm">
            Nenhuma sessão encontrada. Crie uma nova conexão para começar.
          </div>
        ) : (
          sortedSessions.map((session) => {
            const status = getStatusBadge(session.status);
            const title = session.me?.pushName || session.displayName || session.name;
            const number = formatWhatsAppNumber(session.me?.id);
            const onlineLabel = formatOnlineTime(session.updatedAt, session.status);
            const actionState = loadingBySession[session.name];

            return (
              <div
                key={session.name}
                className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-150 hover:border-slate-300 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${status.className}`}>
                      {status.label}
                    </span>

                    <h3 className="mt-4 text-lg font-semibold text-slate-900">{title}</h3>

                    <div className="mt-3 space-y-2 text-sm text-slate-500">
                      <p>
                        <span className="font-medium text-slate-700">WhatsApp:</span> {number}
                      </p>
                      <p>
                        <span className="font-medium text-slate-700">Online há:</span> {onlineLabel}
                      </p>
                    </div>
                  </div>

                  <div className="relative">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setOpenMenuFor((current) => (current === session.name ? null : session.name));
                      }}
                      className="flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                      aria-label="Abrir menu de ações"
                    >
                      <span className="text-xl leading-none">⋮</span>
                    </button>

                    {openMenuFor === session.name && (
                      <div
                        className="absolute right-0 z-10 mt-2 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white py-2 shadow-lg"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => restartSession(session.name)}
                          className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                        >
                          Reiniciar sessão
                        </button>
                        <button
                          type="button"
                          onClick={() => disconnectSession(session.name)}
                          className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                        >
                          Desconectar
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setOpenMenuFor(null);
                            setConfirmDeleteSession(session);
                          }}
                          className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                        >
                          Excluir sessão
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => reconnectSession(session)}
                    disabled={session.status === 'WORKING' || Boolean(actionState)}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {actionState === 'reconnect' ? 'Conectando...' : 'Conectar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => refreshSession(session.name)}
                    disabled={Boolean(actionState)}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {actionState === 'refresh' ? 'Atualizando...' : 'Atualizar'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {createSessionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-semibold text-slate-900">Nova sessão</h2>
            <p className="mt-1 text-sm text-slate-500">Crie uma nova conexão para centralizar esse usuário na lista.</p>

            <div className="mt-6 space-y-4">
              <div>
                <label htmlFor="session-provider" className="mb-2 block text-sm font-medium text-slate-700">
                  Provedor
                </label>
                <select
                  id="session-provider"
                  value={newSessionProvider}
                  onChange={(e) => setNewSessionProvider(e.target.value as 'WAHA' | 'EVOLUTION')}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={isCreating}
                >
                  <option value="EVOLUTION">Evolution</option>
                  <option value="WAHA">WAHA</option>
                </select>
              </div>

              <div>
                <label htmlFor="session-name" className="mb-2 block text-sm font-medium text-slate-700">
                  Nome da sessão
                </label>
                <input
                  id="session-name"
                  type="text"
                  value={newSessionName}
                  onChange={(e) => setNewSessionName(e.target.value)}
                  placeholder="Ex: oficial-eliton"
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={isCreating}
                  autoFocus
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setCreateSessionModalOpen(false);
                  setNewSessionName('');
                  setNewSessionProvider('EVOLUTION');
                }}
                className="flex-1 rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-200"
                disabled={isCreating}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={createSession}
                className="flex-1 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isCreating || !newSessionName.trim()}
              >
                {isCreating ? 'Criando...' : 'Criar sessão'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteSession && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-slate-900">Excluir sessão</h3>
            <p className="mt-2 text-sm text-slate-500">
              Essa ação remove definitivamente a sessão <strong>{confirmDeleteSession.displayName || confirmDeleteSession.name}</strong>.
            </p>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmDeleteSession(null)}
                className="flex-1 rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-200"
                disabled={loadingBySession[confirmDeleteSession.name] === 'delete'}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={deleteSession}
                className="flex-1 rounded-xl bg-red-600 px-4 py-3 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={loadingBySession[confirmDeleteSession.name] === 'delete'}
              >
                {loadingBySession[confirmDeleteSession.name] === 'delete' ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {qrModalOpen && currentQRSession && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/55 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{currentQRSession.displayName || currentQRSession.name}</h3>
                <p className="text-sm text-slate-500">Escaneie o QR Code para concluir a conexão.</p>
              </div>
              <button
                onClick={() => {
                  setQrModalOpen(false);
                  setCurrentQRSession(null);
                }}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Fechar modal"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-6 text-center">
              {currentQRSession.qr ? (
                <>
                  <div className="inline-block rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <img
                      src={currentQRSession.qr}
                      alt="QR Code WhatsApp"
                      className="mx-auto h-56 w-56 rounded"
                    />
                  </div>
                  <div className="mt-4">
                    {currentQRSession.qrExpiresAt && <QRCountdown expiresAt={currentQRSession.qrExpiresAt} />}
                  </div>
                </>
              ) : (
                <div className="py-10">
                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-emerald-600" />
                  <p className="mt-3 text-sm text-slate-500">Gerando QR Code...</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 border-t border-slate-200 px-6 py-4">
              <button
                onClick={() => openQRModal(currentQRSession.name)}
                className="flex-1 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-medium text-white hover:bg-emerald-700"
              >
                Atualizar
              </button>
              <button
                onClick={() => {
                  setQrModalOpen(false);
                  setCurrentQRSession(null);
                }}
                className="flex-1 rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-200"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
